/**
* ─────────────────────────────────────────────────────────────────────
* FILE: src/pages/Dashboard.tsx
* DESCRIPTION: Main dashboard with stats, analytics charts, and recent
*              activity. Provides overview of media verification
*              activities, AI assessments, and trust metrics.
*
* FEATURES:
* - Key metrics cards with live API data
* - AI vs Human-Created content trends (area chart) - live /identities API
* - Content type distribution (modern donut chart) - live /identities API
* - Recent activity table with sorting, scrollbar, AI/Human status
* - Action menus for each file (View Details wired, Download disabled)
* - Dark/Light mode support (via Header)
* - User profile integration (via Header)
* - Single API call to /v1/identities for all dashboard data
* - Mobile-responsive 2x2 stat grid and adaptive layouts
* - Theme-aligned transparent icon colors and high-contrast tooltips
*
* DATA INTEGRATION:
* - Fetches from GET /v1/identities via getApiClient()
* - Uses same mapping pattern as Library page for consistency
* - All data transformations based on real VideoIdentity structure
* - Loading states and error handling for production readiness
*
* API CONTRACT:
* - Endpoint: ENDPOINTS.IDENTITIES_LIST (/v1/identities)
* - Method: GET
* - Response: { identities: VideoIdentity[], total: number, ... }
* - Auth: Handled by getApiClient() interceptor
*
* @copyright 2026-2026 Anou. All rights reserved.
* @author Anou
* @version 2.2.0
* @created 2026-01-15
* @updated 2026-04-24 - Full live API integration, real time-scale charts,
*                       removed all mock data, aligned with Library page
*                       data fetching pattern, trust_score 0-1 scale support
* @updated 2026-06-11 - Mobile responsiveness: 2x2 stat grid on mobile,
*                       theme-aligned transparent icon colors, high-contrast
*                       tooltips, Content Type tabs with mobile dropdown,
*                       Trends chart filtering (AI/Human/Both), Recent Activity
*                       View Details wired to identities page, Download disabled,
*                       added scrollbar to activity table, balanced spacing.
* @updated 2026-06-11 (Follow-up) - Replaced hardcoded trend values with live
*                       percentages of total library. Added row-tap to open
*                       action menu on mobile. Menu now floats upwards for
*                       the last item to prevent cutoff. Replaced Trust Score
*                       column with AI Score in Recent Activity table.
* * @updated 2026-09-05 Scope-aware dashboard — filters identities
*                       and stats by active workspace org_id via useOrgFilter.
*                       Enables data segregation across Personal / Org / Global.
* ─────────────────────────────────────────────────────────────────────
*/
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useUser } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileCheck,
  Bot,
  Shield,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  MoreVertical,
  Eye,
  Download,
  Trash2,
  Share2,
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
  Activity,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
// ─────────────────────────────────────────────────────────────────────
// API CLIENT & ENDPOINTS - Import from shared utils (same as Library)
// ─────────────────────────────────────────────────────────────────────
import { getApiClient } from '../lib/api/apiClient';
import { ENDPOINTS }    from '../lib/api/endpoints';
import type { VideoIdentity, FileType } from '../lib/api/types';
//Scope-aware data segregation
import { useOrgFilter } from '../hooks/useOrgFilter';
import { useWorkspace } from '../contexts/WorkspaceContext';

// ─────────────────────────────────────────────────────────────────────
// THEME COLORS (Transparent style for sleek UI)
// ─────────────────────────────────────────────────────────────────────
const THEME = {
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    stroke: '#3b82f6',
  },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    stroke: '#10b981',
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
    stroke: '#a855f7',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
    stroke: '#6366f1',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    stroke: '#f59e0b',
  },
};

interface ListResponse {
  identities: VideoIdentity[];
  total: number;
  page: number;
  limit: number;
  filters?: Record<string, any>;
}

// ─────────────────────────────────────────────────────────────────────
// DASHBOARD TYPES - Internal shapes for rendering
// ────────────────────────────────────────────────────────────────────
interface StatCard {
  label: string;
  value: string | number;
  sublabel: string;
  trend: 'up' | 'down' | 'neutral';
  trendValue: string;
  icon: React.ElementType;
  bg: string;
  color: string;
  stroke: string;
}

interface TrendData {
  time: string;
  ai: number;
  human: number;
  timestamp: number; // For proper sorting
}

interface ContentDistribution {
  name: string;
  value: number;
  color: string;
}

interface RecentFile {
  id: string;
  fileName: string;
  type: 'Document' | 'Image' | 'Video' | 'Audio' | 'File';
  aiScore?: number; // NEW: AI confidence score (0-1 scale)
  status: 'ai-generated' | 'human-created';
  date: string;
  size: string;
  persistentId: string;
}

type SortField = 'fileName' | 'type' | 'aiScore' | 'status' | 'date';
type SortOrder = 'asc' | 'desc';

// ────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS - Reusable helpers for data transformation and UI
// ─────────────────────────────────────────────────────────────────────
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const getStatusBadge = (status: RecentFile['status']) => {
  const badges = {
    'ai-generated': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    'human-created': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  };
  return badges[status];
};

const getStatusLabel = (status: RecentFile['status']) => {
  const labels = {
    'ai-generated': 'AI-Generated',
    'human-created': 'Human-Created',
  };
  return labels[status];
};

// NEW: Color helper for AI Score
const getAiScoreColor = (score: number | undefined) => {
  if (score === undefined) return 'text-gray-400';
  if (score >= 0.7) return 'text-red-600 dark:text-red-400'; // High AI confidence
  if (score >= 0.4) return 'text-amber-600 dark:text-amber-400'; // Unclear
  return 'text-emerald-600 dark:text-emerald-400'; // Low AI confidence (Human)
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatNumber = (num: number | string): string => {
  if (typeof num === 'string') return num;
  return num.toLocaleString();
};

// Format date for chart axis - group by day for readability
const formatChartDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

// ─────────────────────────────────────────────────────────────────────
// DATA TRANSFORMERS - Convert API VideoIdentity to dashboard shapes
// ─────────────────────────────────────────────────────────────────────
const mapIdentityToRecentFile = (identity: VideoIdentity): RecentFile => {
  // Determine AI status from backend origin detection
  const isAiGenerated =
    identity.origin?.is_ai_generated ??
    identity.origin?.source_detection?.ai_generated ??
    false; 

  // Extract AI Score from VMIE or origin source detection
  const aiScore =       
    identity.vmie_detection?.total_score ?? 
    identity.origin?.source_detection?.ai_confidence;

  // Map file_type to UI-friendly type
  const typeMap: Record<string, RecentFile['type']> = {
    'video': 'Video',
    'image': 'Image',
    'audio': 'Audio',
    'document': 'Document',
    'file': 'File',
  };

  return {
    id: identity.persistent_id,
    fileName: identity.original_filename || 'Unknown',
    type: typeMap[identity.file_type] || 'File',
    aiScore: aiScore,
    status: isAiGenerated ? 'ai-generated' : 'human-created',
    date: identity.created_at,
    size: formatBytes(identity.file_size || 0),
    persistentId: identity.persistent_id,
  };
};

const transformToTrendData = (identities: VideoIdentity[]): TrendData[] => {
  // Group by day for chart display
  const grouped = identities.reduce((acc, item) => {
    const dateKey = new Date(item.created_at).toISOString().split('T')[0]; // YYYY-MM-DD
    const displayLabel = formatChartDate(item.created_at);
    if (!acc[dateKey]) {
      acc[dateKey] = {
        time: displayLabel,
        ai: 0,
        human: 0,
        timestamp: new Date(item.created_at).getTime()
      };
    }

    // Determine AI status from backend origin detection
    const isAiGenerated =
      item.origin?.is_ai_generated ??
      item.origin?.source_detection?.ai_generated ??
      false;

    if (isAiGenerated) {
      acc[dateKey].ai += 1;
    } else {
      acc[dateKey].human += 1;
    }
    return acc;
  }, {} as Record<string, TrendData>);

  // Convert to array and sort by timestamp
  return Object.values(grouped)
    .sort((a, b) => a.timestamp - b.timestamp)
    .slice(-7); // Show last 7 days for readability
};

const transformToContentDistribution = (identities: VideoIdentity[]): ContentDistribution[] => {
  const counts = identities.reduce((acc, item) => {
    const type = item.file_type || 'file';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Theme-aligned colors
  const colors: Record<string, string> = {
    'video': THEME.indigo.stroke,
    'image': THEME.emerald.stroke,
    'audio': THEME.blue.stroke,
    'document': THEME.purple.stroke,
    'file': '#718096',
  };

  return Object.entries(counts).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    color: colors[name] || '#718096',
  }));
};

const transformToLiveStats = (identities: VideoIdentity[]) => {
  const totalUploads = identities.length;
  // Trust score threshold: >0.1 = trusted (adjustable)
  const trustedCount = identities.filter(f => (f.trust_score ?? 0) > 0.1).length;
    const aiCount = identities.filter(item =>
    item.origin?.is_ai_generated ??
    item.origin?.source_detection?.ai_generated ??
    false
  ).length;
  // Verified = trust_score >= 0.7 (high confidence)
  const verifiedCount = identities.filter(f => (f.trust_score ?? 0) >= 0.7).length;

  return {
    totalUploads,
    trustedCount,
    aiCount,
    verifiedCount,
    aiPercentage: totalUploads > 0 ? Math.round((aiCount / totalUploads) * 100) : 0,
  };
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user } = useUser();
  const navigate = useNavigate();
  const [recentFiles, setRecentFiles] = useState<RecentFile[]>([]);
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [contentDistribution, setContentDistribution] = useState<ContentDistribution[]>([]);
  const [liveStats, setLiveStats] = useState<{
    totalUploads: number;
    trustedCount: number;
    aiCount: number;
    verifiedCount: number;
    aiPercentage: number;
  }>({ totalUploads: 0, trustedCount: 0, aiCount: 0, verifiedCount: 0, aiPercentage: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // New UI States for Charts
  const [distributionView, setDistributionView] = useState<'type' | 'ai'>('ai');
  const [trendFilter, setTrendFilter] = useState<'all' | 'ai' | 'human'>('all');

  // State to track if the user has expanded the cards.
  const [showAllStats, setShowAllStats] = useState(false);
  // Phase 2.5: Scope-aware data segregation
  const orgFilter = useOrgFilter();
  const { scope } = useWorkspace();

  // ──────────────────────────────────────────────────────────────────
  // EFFECTS - Fetch live data from /v1/identities API
  // ───────────────────────────────────────────────────────────────────
  const loadDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiClient = getApiClient();
      // Fetch all identities for dashboard (no pagination limit for stats)
      // In production, you might add date filters here for performance
      // Scope-aware filter — only show identities from active workspace
      const apiParams: Record<string, any> = { limit: 100 };
      if (orgFilter) {
        apiParams.org_id = orgFilter;
      }
      const response = await apiClient.get<ListResponse>(
        ENDPOINTS.IDENTITIES_LIST,
        apiParams
      );
      const identities = response.identities;

      // Transform and set all dashboard data
      setRecentFiles(identities.slice(0, 50).map(mapIdentityToRecentFile));
      setTrendData(transformToTrendData(identities));
      setContentDistribution(transformToContentDistribution(identities));
      setLiveStats(transformToLiveStats(identities));
    } catch (error: any) {
      console.error('Dashboard data fetch error:', error);
      // In production, you might show a toast or retry logic here
      // For now, keep empty state - no mock fallback per requirements
    } finally {
      setIsLoading(false);
    }
  }, [orgFilter]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // ───────────────────────────────────────────────────────────────────
  // MEMOIZED COMPUTATIONS - Optimize re-renders
  // ───────────────────────────────────────────────────────────────────
  const sortedFiles = useMemo(() => {
    return [...recentFiles].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'fileName':
          comparison = a.fileName.localeCompare(b.fileName);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'aiScore':
          comparison = (a.aiScore ?? 0) - (b.aiScore ?? 0);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [recentFiles, sortField, sortOrder]);

  // Compute AI Distribution for the pie chart
  const aiDistribution: ContentDistribution[] = useMemo(() => {
    const aiCount = liveStats.aiCount;
    const humanCount = liveStats.totalUploads - aiCount;
    return [
      { name: 'AI Generated', value: aiCount, color: THEME.indigo.stroke },
      { name: 'Human Created', value: humanCount, color: THEME.emerald.stroke },
    ];
  }, [liveStats]);

  // ──────────────────────────────────────────────────────────────────
  // HANDLERS
  // ──────────────────────────────────────────────────────────────────
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // FIXED: View Details now navigates to the identity page
  const handleViewFile = (fileId: string) => {
    navigate(`/identities/${fileId}`);
    setActiveMenuId(null);
  };

  // FIXED: Download is disabled
  const handleDownload = (file: RecentFile) => {
    // Disabled per requirements
    console.log('Download disabled for file:', file.fileName);
    setActiveMenuId(null);
  };

  const handleDelete = (fileId: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      setRecentFiles(prev => prev.filter(f => f.id !== fileId));
      setActiveMenuId(null);
    }
  };

  const handleShare = (file: RecentFile) => {
    const shareLink = `${window.location.origin}/identities/${file.id}`;
    navigator.clipboard.writeText(shareLink);
    alert('Share link copied to clipboard!');
    setActiveMenuId(null);
  };

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Loading State
  // ───────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="pt-[80px] px-4 sm:px-6 lg:px-8 pb-8 max-w-7xl mx-auto w-full">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Main Dashboard
  // ───────────────────────────────────────────────────────────────────
  
  // Calculate live percentages for stat cards
  const verifiedPercentage = liveStats.totalUploads > 0 ? Math.round((liveStats.verifiedCount / liveStats.totalUploads) * 100) : 0;
  const trustedPercentage = liveStats.totalUploads > 0 ? Math.round((liveStats.trustedCount / liveStats.totalUploads) * 100) : 0;

  return (
    <div className="pt-[80px] px-4 sm:px-6 lg:px-8 pb-8 max-w-7xl mx-auto w-full">
      {/* Page Title */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          {/* Phase 2.5: Scope indicator */}
          {scope !== 'global' && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {scope === 'personal' ? 'Personal' : 'Organization'}
            </span>
          )}
        </div>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Welcome back! Here's what's happening with your media verification.
        </p>
      </div>

      {/* Stats Grid - Live API Data - 2x2 on mobile, 4-across on desktop */}
      {/* Stats Section - Mobile Collapsible / Desktop Grid */}
      <div className="mb-6">
        
        {/* MOBILE ONLY: Single Summary Card & Expand Button */}
        <div className="sm:hidden">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between mb-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">Total Uploads</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatNumber(liveStats.totalUploads)}
                </p>
              </div>
              <div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${THEME.blue.bg}`}>
                <Activity className={`w-5 h-5 sm:w-6 sm:h-6 ${THEME.blue.text}`} />
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs sm:text-sm">
              <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="truncate font-medium">Total Library</span>
              </div>
              <span className="text-gray-500 dark:text-gray-500 truncate ml-1 hidden sm:inline">{liveStats.totalUploads} total files</span>
            </div>
          </div>
          
          {/* Expand/Collapse Button */}
          <button
            onClick={() => setShowAllStats(!showAllStats)}
            className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
          >
            {showAllStats ? 'Show Less' : 'View All Metrics'}
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showAllStats ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* FULL GRID: Hidden on mobile unless expanded, always visible on tablet/desktop */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 ${showAllStats ? 'grid' : 'hidden sm:grid'}`}>
          {[
            {
              label: 'Total Uploads',
              value: liveStats.totalUploads,
              sublabel: `${liveStats.totalUploads} total files`,
              trend: 'up' as const,
              trendValue: 'Total Library',
              icon: Activity,
              bg: THEME.blue.bg,
              color: THEME.blue.text,
              stroke: THEME.blue.stroke,
            },
            {
              label: 'Verification Volume',
              value: liveStats.verifiedCount,
              sublabel: `${liveStats.verifiedCount} verified`,
              trend: 'up' as const,
              trendValue: `${verifiedPercentage}% verified`,
              icon: FileCheck,
              bg: THEME.emerald.bg,
              color: THEME.emerald.text,
              stroke: THEME.emerald.stroke,
            },
            {
              label: 'AI Assessments',
              value: `${liveStats.aiPercentage}%`,
              sublabel: `${liveStats.aiCount} processed`,
              trend: 'up' as const,
              trendValue: `${liveStats.aiPercentage}% AI detected`,
              icon: Bot,
              bg: THEME.purple.bg,
              color: THEME.purple.text,
              stroke: THEME.purple.stroke,
            },
            {
              label: 'Trusted Content',
              value: liveStats.trustedCount,
              sublabel: 'Trust Score > 0.1',
              trend: liveStats.trustedCount > 0 ? 'up' : 'neutral' as const,
              trendValue: `${trustedPercentage}% trusted`,
              icon: Shield,
              bg: THEME.indigo.bg,
              color: THEME.indigo.text,
              stroke: THEME.indigo.stroke,
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">{stat.label}</p>
                  <p className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(stat.value)}
                  </p>
                </div>
                <div className={`p-2 sm:p-3 rounded-lg shrink-0 ml-2 ${stat.bg}`}>
                  <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.color}`} />
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs sm:text-sm">
                <div
                  className={`flex items-center gap-1 ${
                    stat.trend === 'up'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : stat.trend === 'down'
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {stat.trend === 'up' ? (
                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  ) : stat.trend === 'down' ? (
                    <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
                  ) : null}
                  <span className="truncate font-medium">{stat.trendValue}</span>
                </div>
                <span className="text-gray-500 dark:text-gray-500 truncate ml-1 hidden sm:inline">{stat.sublabel}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Charts Section - Swapped positions: Trends on Left, Distribution on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        
        {/* Area Chart - AI vs Human-Created Trends (Live Data, Real Time Scale) - LEFT */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              AI vs Human-Created Content Trends
            </h2>
            <select
              value={trendFilter}
              onChange={(e) => setTrendFilter(e.target.value as 'all' | 'ai' | 'human')}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Both</option>
              <option value="ai">AI Only</option>
              <option value="human">Human Only</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="colorAi" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={THEME.indigo.stroke} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={THEME.indigo.stroke} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorHuman" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={THEME.emerald.stroke} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={THEME.emerald.stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
              <XAxis
                dataKey="time"
                stroke="#6b7280"
                fontSize={11}
                interval="preserveStartEnd"
              />
              <YAxis stroke="#6b7280" fontSize={11} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  color: '#111827',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                labelFormatter={(label) => `Date: ${label}`}
              />
              {(trendFilter === 'all' || trendFilter === 'ai') && (
                <Area
                  type="monotone"
                  dataKey="ai"
                  stroke={THEME.indigo.stroke}
                  fill="url(#colorAi)"
                  strokeWidth={2}
                  name="AI-Generated"
                />
              )}
              {(trendFilter === 'all' || trendFilter === 'human') && (
                <Area
                  type="monotone"
                  dataKey="human"
                  stroke={THEME.emerald.stroke}
                  fill="url(#colorHuman)"
                  strokeWidth={2}
                  name="Human-Created"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-6 mt-4">
            {(trendFilter === 'all' || trendFilter === 'ai') && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: THEME.indigo.stroke }} />
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">AI-Generated</span>
              </div>
            )}
            {(trendFilter === 'all' || trendFilter === 'human') && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: THEME.emerald.stroke }} />
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Human-Created</span>
              </div>
            )}
          </div>
        </div>

        {/* Pie Chart - Content Type Distribution (Modernized, Live Data) - RIGHT */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
              Content Type Distribution
            </h2>
            {/* Desktop: Tab buttons */}
            <div className="hidden sm:flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">  
              <button
                onClick={() => setDistributionView('ai')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  distributionView === 'ai'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                AI vs Human
              </button>
              <button
                onClick={() => setDistributionView('type')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  distributionView === 'type'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Content Type
              </button>
            </div>
          </div>

          {/* Mobile: Dropdown selector */}
          <div className="sm:hidden mb-4">
            <select
              value={distributionView}
              onChange={(e) => setDistributionView(e.target.value as 'type' | 'ai')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="type">Content Type</option>
              <option value="ai">AI vs Human</option>
            </select>
          </div>

          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={distributionView === 'type' ? contentDistribution : aiDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                outerRadius={70}
                innerRadius={40}
                paddingAngle={2}
                fill="#8884d8"
                dataKey="value"
              >
                {(distributionView === 'type' ? contentDistribution : aiDistribution).map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    fillOpacity={0.85}
                    stroke="transparent"
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.75rem',
                  color: '#111827',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
              />
              <Legend
                wrapperStyle={{
                  paddingTop: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '16px'
                }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity Table - Live Data, Scrollable, AI/Human Status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            Recent Activity
          </h2>
        </div>
        {/* ADDED: max-h and overflow-y-auto for scrollbar after ~4 items */}
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto custom-scrollbar">
          <table className="w-full">
            <thead className="sticky top-0 bg-white dark:bg-gray-800 z-10">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              {/* Actions column moved to LEFT */}
              <th className="text-left py-4 px-4 sm:px-6 text-sm font-medium text-gray-600 dark:text-gray-400 w-16">
                Actions
              </th>
              {/* File Name with truncation */}
              <th className="text-left py-4 px-4 sm:px-6">
                <button
                  onClick={() => handleSort('fileName')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  File Name
                  {sortField === 'fileName' &&
                    (sortOrder === 'asc' ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    ))}
                </button>
              </th>
              {/* Keep other headers as they are... */}
              <th className="text-left py-4 px-4 sm:px-6 hidden md:table-cell">
                <button
                  onClick={() => handleSort('type')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Type
                  {sortField === 'type' &&
                    (sortOrder === 'asc' ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    ))}
                </button>
              </th>
              <th className="text-left py-4 px-4 sm:px-6">
                <button
                  onClick={() => handleSort('aiScore')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  AI Score
                  {sortField === 'aiScore' &&
                    (sortOrder === 'asc' ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    ))}
                </button>
              </th>
              <th className="text-left py-4 px-4 sm:px-6 hidden sm:table-cell">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Status
                  {sortField === 'status' &&
                    (sortOrder === 'asc' ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    ))}
                </button>
              </th>
              <th className="text-left py-4 px-4 sm:px-6 hidden lg:table-cell">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Date
                  {sortField === 'date' &&
                    (sortOrder === 'asc' ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    ))}
                </button>
              </th>
            </tr>
          </thead>
            <tbody>
            {sortedFiles.map((file, index) => {
              const isLastItem = index === sortedFiles.length - 1;
              return (
                <tr
                  key={file.id}
                  onClick={() => setActiveMenuId(activeMenuId === file.id ? null : file.id)}
                  className={`border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${
                    activeMenuId === file.id ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  }`}
                >
                  {/* Actions column MOVED TO LEFT */}
                  <td className="py-4 px-4 sm:px-6">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuId(activeMenuId === file.id ? null : file.id);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {activeMenuId === file.id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setActiveMenuId(null)}
                          />
                          {/* CHANGED: right-0 to left-0 so menu opens rightwards over the file name */}
                          <div className={`absolute left-0 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 ${
                            isLastItem ? 'bottom-full mb-2' : 'top-full mt-2'
                          }`}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewFile(file.id);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              View Details
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(file);
                              }}
                              disabled
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50"
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(file);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <Share2 className="w-4 h-4" />
                              Share Link
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(file.id);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-b-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  
                  {/* File Name with TRUNCATION */}
                  <td className="py-4 px-4 sm:px-6">
                  <div>
                    {/* ADDED: truncate max-w-[120px] to match the hash length exactly on mobile */}
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[120px]">
                      {file.fileName}
                    </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                          {file.size}
                        </span>
                        <span className="text-xs text-gray-400 hidden sm:inline">•</span>
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <span className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                            {file.persistentId}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(file.persistentId, file.id);
                            }}
                            className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded shrink-0"
                          >
                            {copiedId === file.id ? (
                              <Check className="w-3 h-3 text-green-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  {/* Keep other cells in same order... */}
                  <td className="py-4 px-4 sm:px-6 text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">
                    {file.type}
                  </td>
                  <td className="py-4 px-4 sm:px-6">
                    <span className={`text-sm font-medium ${getAiScoreColor(file.aiScore)}`}>
                      {file.aiScore !== undefined ? file.aiScore.toFixed(2) : '-'}
                    </span>
                  </td>
                  <td className="py-4 px-4 sm:px-6 hidden sm:table-cell">
                    <span
                      className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(
                        file.status
                      )}`}
                    >
                      {getStatusLabel(file.status)}
                    </span>
                  </td>
                  <td className="py-4 px-4 sm:px-6 text-sm text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                    {formatDate(file.date)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
        {/* Empty State */}
        {sortedFiles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No files yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Upload your first file to get started
            </p>
            <button
              onClick={() => navigate('/analyze')}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              Upload File
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;